/**
 * MyJobList — v49.5 "See all" page (browse + search).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, Search, MapPin, Building2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';
import { JOB_L, contractLabel } from '../components/MyJob/i18n';
import Navigation from '../components/Navigation';

const MyJobList = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const l = JOB_L[language] || JOB_L.fr;
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { const t = setTimeout(() => setDebounced(q), 300); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // v49.8 — Send app language so backend can scope the feed to that language
      // (auto-bypassed when `q` is present because the user is searching).
      const r = await axios.get('/api/myjob/offers', {
        params: { q: debounced || '', limit: 50, lang: language || 'fr' },
        headers: { 'X-App-Language': language || 'fr' },
      });
      setItems(r.data.items || []);
    } catch (_) { /* silent */ } finally { setLoading(false); }
  }, [debounced, language]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="myjob-list-page">
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur border-b border-white/10">
        <div className="px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/films?tab=studio')} className="p-2 rounded-full hover:bg-white/10" data-testid="myjob-list-back"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-lg font-bold flex items-center gap-2"><Briefcase className="w-5 h-5 text-amber-400" /> {l.title}</h1>
        </div>
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={l.search} className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-amber-500" data-testid="myjob-list-search" />
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-40 rounded-2xl bg-white/5 animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16"><Briefcase className="w-12 h-12 mx-auto mb-2 text-gray-500" /><p className="text-sm text-gray-400">{l.empty}</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map(o => (
              <button key={o.id} onClick={() => navigate(`/myjob/${o.id}`)} className="text-left rounded-2xl overflow-hidden bg-white/5 hover:bg-white/10 border border-white/10 transition-colors" data-testid={`myjob-list-card-${o.id}`}>
                <div className="relative h-32 bg-gradient-to-br from-amber-700/30 to-orange-800/30">
                  {o.cover_url ? <img src={getMediaUrl(o.cover_url)} alt={o.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Briefcase className="w-10 h-10 text-white/30" /></div>}
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-[10px] font-semibold">{contractLabel(o.contract_type, language)}</span>
                </div>
                <div className="p-3 space-y-1">
                  <h4 className="font-semibold text-sm line-clamp-2">{o.title}</h4>
                  {o.company_name && <p className="text-xs text-gray-400 flex items-center gap-1"><Building2 className="w-3 h-3" /> {o.company_name}</p>}
                  {o.location && <p className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.location}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
      <Navigation />
    </div>
  );
};

export default MyJobList;
